import React, { FC, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { TableRow, TableCell, TextField, MenuItem } from '@material-ui/core';

import { ReasonCodes } from 'types/ReasonCodes';
import { getReasonCodes } from 'reducers/reasonCodesDB';
import { setOneOrderSelected } from 'actions/returnProducts';
import { PluginComponent } from 'plugins';
import { ReturnRow } from 'types/ReturnTable';
import { REASONS } from 'constants/reasonCodesDB';
import { getProductCodeToShowOnReturnModal } from 'reducers/configs/settings';

import { toggleReturnRow, setRowReasonID } from '../../utils';
import { Checkbox, StyledTableRow } from '../styled';

import RowAmount from './RowAmount';

type OrderProductProps = {
  row: ReturnRow;
  globalReturnReason: string | undefined;
  isEven: boolean;
  hideReturnReasons: boolean;
  depthLevel?: number;
};

const OrderProduct: FC<OrderProductProps> = ({
  row,
  globalReturnReason,
  isEven,
  hideReturnReasons = false,
  depthLevel = 1,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('return');
  const codeToShow = useSelector(getProductCodeToShowOnReturnModal);

  const isSelected = !!row?.selected;
  const returnReasonID = row?.returnReasonID;
  const returnReasons: ReasonCodes[] = useSelector(
    getReasonCodes(REASONS.RETURN),
  );

  const returnReasonsOptions = useMemo(
    () =>
      returnReasons.map(reason => (
        <MenuItem
          data-testid="product-row-reason-option"
          data-test-key={reason.reasonID}
          key={reason.reasonID}
          value={reason.reasonID}
        >
          {reason.name}
        </MenuItem>
      )),
    [returnReasons],
  );

  const rowLocked = row.remainingAmount === 0 || !(row.isReturnable ?? true);
  const originalQuantity = row.originalAmount;

  useEffect(() => {
    if (row.remainingAmount < row?.amount) {
      // If the selected amount exceeds the available amount, cap that
      dispatch(
        setOneOrderSelected(row.stableRowID, {
          ...row,
          amount: row.remainingAmount,
        }),
      );
    }
  }, [dispatch, row]);

  return (
    <PluginComponent
      hookname="UIReturnOrderProduct"
      props={{
        row,
        globalReturnReason,
        isSelected,
        isEven,
        depthLevel,
        hideReturnReasons,
        rowLocked,
        returnReasonID,
        toggleReturnRow: () =>
          dispatch(toggleReturnRow(row, globalReturnReason)),
      }}
    >
      <>
        <StyledTableRow
          data-testid="product-return-row"
          className={classNames('grouped', { rowLocked, isEven })}
        >
          <PluginComponent
            hookname="UISingleReturnRowCheckbox"
            props={{
              row,
              rowLocked,
              isSelected,
              globalReturnReason,
            }}
          >
            <TableCell align="center">
              <Checkbox
                checked={row.selected ?? false}
                data-testid="product-row-toggle"
                onChange={e => {
                  e.stopPropagation();
                  dispatch(toggleReturnRow(row, globalReturnReason));
                }}
                invisible={rowLocked}
              />
            </TableCell>
          </PluginComponent>
          <TableCell
            data-testid="product-row-name"
            style={{
              paddingLeft: depthLevel ? `${depthLevel * 16}px` : '4px',
              fontWeight: depthLevel === 1 ? 700 : 'initial',
            }}
          >
            {row.itemName}
          </TableCell>
          <TableCell align="center" data-testid="product-row-barcode">
            {row[codeToShow]?.split(',').join(', ')}
          </TableCell>
          <TableCell align="right" data-testid="product-row-original-qty">
            {originalQuantity}
          </TableCell>
          <TableCell align="center">
            <RowAmount row={row} />
          </TableCell>
          {returnReasons.length > 0 && !hideReturnReasons ? (
            <TableCell align="center">
              {isSelected ? (
                <TextField
                  onChange={e => dispatch(setRowReasonID(row, e.target.value))}
                  className={!isSelected ? 'isHidden' : ''}
                  select
                  data-testid="product-row-return-reasons"
                  disabled={!isSelected}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={row?.returnReasonID ?? 0}
                >
                  <MenuItem value={0}>{t('options.default')}</MenuItem>
                  {returnReasonsOptions}
                </TextField>
              ) : null}
            </TableCell>
          ) : null}
        </StyledTableRow>
        {row.children.map(ch => (
          <OrderProduct
            row={ch}
            isEven={isEven}
            hideReturnReasons={hideReturnReasons}
            depthLevel={depthLevel + 1}
            globalReturnReason={globalReturnReason}
          />
        ))}
      </>
    </PluginComponent>
  );
};

export default OrderProduct;
